import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, Plus, Pencil, Trash2, X, Save } from "lucide-react";
import CampaignModal from "@/components/CampaignModal";
import StateSelector from "@/components/StateSelector";
import AdminMerch from "@/pages/AdminMerch";
import AdminMenuTemplates from "@/pages/AdminMenuTemplates";
import ModalShell from "@/components/ModalShell";
import { confirmDialog } from "@/components/ConfirmDialog";
import BackButton from "@/components/BackButton";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "marketing", subcategory: "", tags: "", states: "" });
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);

  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  // Users state
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadCampaigns = () => api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => {});

  useEffect(() => { load(); loadCampaigns(); }, []);

  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    // Wait a tick so nested sections have mounted before scrolling.
    const id = setTimeout(() => {
      const el = document.getElementById(location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(id);
  }, [location.hash]);

  async function deleteUser(u) {
    const ok = await confirmDialog({ title: `Delete user ${u.name}?`, description: `${u.email} will lose access. This cannot be undone.` });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function openNewCampaign() { setEditingCampaign(null); setCampaignModalOpen(true); }
  function openEditCampaign(c) { setEditingCampaign(c); setCampaignModalOpen(true); }

  async function deleteCampaign(c) {
    const ok = await confirmDialog({ title: `Delete campaign "${c.name}"?`, description: "This cannot be undone." });
    if (!ok) return;
    try {
      await api.delete(`/admin/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      setCampaigns((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function handleCampaignSaved(evt) {
    if (evt?.created) setCampaigns((prev) => [evt.created, ...prev]);
    else if (evt?.updated) setCampaigns((prev) => prev.map((c) => (c.id === evt.updated.id ? evt.updated : c)));
    else loadCampaigns();
  }

  async function updateUser(uid, patch) {
    setBusy(true);
    try { await api.patch(`/admin/users/${uid}`, patch); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }

  async function upload() {
    if (!file) return toast.error("Select a file");
    if (!form.title) return toast.error("Add a title");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    try {
      await api.post("/admin/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setFile(null); setForm({ title: "", description: "", category: "marketing", subcategory: "", tags: "", states: "" });
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  }

  return (
    <div className="space-y-10 slide-up" data-testid="admin-page">
      <BackButton />
      <header>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Control Room</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase font-bold tracking-tight">Admin</h1>
        <p className="text-muted-foreground mt-2">Upload assets, manage users, and configure the portal.</p>
      </header>

      <section className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-border rounded-sm bg-surface p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Upload</div>
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
            className={`border-2 border-dashed rounded-sm p-10 text-center transition ${drag ? "border-foreground bg-surface-elev" : "border-border"}`}
            data-testid="dropzone"
          >
            <UploadCloud className="h-8 w-8 mx-auto text-muted-foreground" />
            <div className="mt-3 text-sm">{file ? file.name : "Drag & drop or click to select"}</div>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
            <button onClick={() => fileRef.current?.click()} className="mt-3 border border-border px-4 py-1.5 text-xs uppercase tracking-widest font-mono hover:bg-surface-elev transition" data-testid="pick-file">Select File</button>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="upload-title" />
            <Select label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} options={["product", "strain", "marketing", "retail", "sales", "merch"]} testId="upload-cat" />
            <Input label="Subcategory" value={form.subcategory} onChange={(v) => setForm({ ...form, subcategory: v })} testId="upload-sub" />
            <Input label="Tags (comma-sep)" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} testId="upload-tags" />
            <Input label="States (comma-sep)" value={form.states} onChange={(v) => setForm({ ...form, states: v })} testId="upload-states" />
          </div>
          <Input label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} className="mt-4" testId="upload-desc" />

          <button
            onClick={upload}
            disabled={uploading}
            className="mt-6 bg-foreground text-background px-6 py-3 uppercase tracking-widest text-xs font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="upload-submit"
          >
            {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload Asset
          </button>
        </div>

        <div className="border border-border rounded-sm bg-surface p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Quick Actions</div>
          <ul className="text-sm space-y-3">
            <li className="flex justify-between"><span className="text-muted-foreground">Users</span><span className="font-semibold">{users.length}</span></li>
            <li className="border-t border-border pt-3">
              <p className="text-xs text-muted-foreground">Tip: use tags like <code className="font-mono">summer-solstice</code> to group assets under a campaign.</p>
            </li>
          </ul>
        </div>
      </section>

      <section className="border border-border rounded-sm bg-surface p-6" data-testid="admin-campaigns">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Campaigns</div>
            <p className="text-xs text-muted-foreground mt-1">Featured campaigns appear on the Dashboard for all users. Non-featured campaigns are still searchable.</p>
          </div>
          <button onClick={openNewCampaign} className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2" data-testid="new-campaign-btn">
            <Plus className="h-3.5 w-3.5" /> New Campaign
          </button>
        </div>

        {campaigns.length === 0 && (
          <div className="text-sm text-muted-foreground py-10 text-center border border-dashed border-border rounded-sm">
            No campaigns yet. Create the first one above.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaigns.map((c) => (
            <div key={c.id} className="border border-border rounded-sm overflow-hidden bg-background group" data-testid={`campaign-row-${c.id}`}>
              <div className="aspect-[4/3] bg-muted relative overflow-hidden">
                {c.hero_image ? (
                  <img src={c.hero_image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs font-mono uppercase tracking-widest">No image</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute top-2 left-2 flex gap-1.5">
                  <span className="w-2 h-2 rounded-full ring-1 ring-black/30" style={{ background: c.color }} />
                  <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-black/70 text-white rounded-sm">{c.status}</span>
                  {c.featured && <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-white text-black rounded-sm">Featured</span>}
                </div>
                <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition">
                  <button onClick={() => openEditCampaign(c)} className="p-1.5 rounded-sm bg-white text-black" data-testid={`campaign-edit-${c.id}`} aria-label="Edit"><Pencil className="h-3 w-3" /></button>
                  <button onClick={() => deleteCampaign(c)} className="p-1.5 rounded-sm text-white" style={{ background: "#E15C2C" }} data-testid={`campaign-delete-${c.id}`} aria-label="Delete"><Trash2 className="h-3 w-3" /></button>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-3 text-white">
                  <div className="font-display text-xl uppercase font-bold tracking-tight leading-tight">{c.name}</div>
                  {c.tagline && <div className="text-xs opacity-80 mt-0.5 truncate">{c.tagline}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

    <div id="merch" className="scroll-mt-24">
      <AdminMerch />
    </div>

      <section className="border border-border rounded-sm bg-surface p-6">
        <AdminMenuTemplates />
      </section>

      <section className="border border-border rounded-sm bg-surface p-6">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Users</div>
            <p className="text-xs text-muted-foreground mt-1">Manage partner access. Admins see all locations. Partners see only their assigned locations plus generic (GEN) assets.</p>
          </div>
          <button onClick={() => { setEditingUser(null); setUserModalOpen(true); }} className="bg-foreground text-background px-4 py-2 text-xs font-semibold tracking-widest uppercase flex items-center gap-2" data-testid="new-user-btn">
            <Plus className="h-3.5 w-3.5" /> New User
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Access</th>
                <th className="py-2 pr-4">Location</th>
                <th className="py-2 pr-4 w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => <UserRow key={u.id} user={u} onEdit={() => { setEditingUser(u); setUserModalOpen(true); }} onDelete={() => deleteUser(u)} />)}
            </tbody>
          </table>
        </div>
      </section>

      <CampaignModal
        open={campaignModalOpen}
        campaign={editingCampaign}
        onClose={() => setCampaignModalOpen(false)}
        onSaved={handleCampaignSaved}
      />

      <UserModal
        open={userModalOpen}
        user={editingUser}
        onClose={() => setUserModalOpen(false)}
        onSaved={(evt) => {
          if (evt?.created) setUsers((prev) => [...prev, evt.created]);
          else if (evt?.updated) setUsers((prev) => prev.map((x) => (x.id === evt.updated.id ? evt.updated : x)));
          else load();
        }}
      />
    </div>
  );
}

function UserRow({ user, onEdit, onDelete }) {
  return (
    <tr className="border-b border-border" data-testid={`user-row-${user.id}`}>
      <td className="py-2.5 pr-4 font-semibold">{user.name}</td>
      <td className="py-2.5 pr-4 text-muted-foreground">{user.email}</td>
      <td className="py-2.5 pr-4">
        <span className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest ${user.role === "admin" ? "bg-foreground text-background" : "bg-surface-elev text-muted-foreground"}`}>{user.role}</span>
      </td>
      <td className="py-2.5 pr-4">
        <div className="flex flex-wrap gap-1">
          {(user.states || []).length === 0 && <span className="text-xs text-muted-foreground">—</span>}
          {(user.states || []).map((s) => (
            <span key={s} className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${s === "GEN" ? "border-foreground text-foreground" : "border-border text-muted-foreground"}`}>{s}</span>
          ))}
        </div>
      </td>
      <td className="py-2.5 pr-4">
        <div className="flex gap-1.5">
          <button onClick={onEdit} className="p-1.5 border border-border rounded-sm hover:bg-surface-elev" data-testid={`user-edit-${user.id}`} aria-label="Edit"><Pencil className="h-3 w-3" /></button>
          <button onClick={onDelete} className="p-1.5 text-white rounded-sm hover:opacity-90" style={{ background: "#E15C2C" }} data-testid={`user-delete-${user.id}`} aria-label="Delete"><Trash2 className="h-3 w-3" /></button>
        </div>
      </td>
    </tr>
  );
}

function UserModal({ open, user, onClose, onSaved }) {
  const isEdit = Boolean(user);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "partner", states: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", email: user.email || "", password: "", role: user.role || "partner", states: (user.states || []).join(", ") });
    } else if (open) {
      setForm({ name: "", email: "", password: "", role: "partner", states: "" });
    }
  }, [user, open]);

  if (!open) return null;

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.email.trim()) return toast.error("Email is required");
    setBusy(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      role: form.role,
      states: form.states.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isEdit) {
        const { data } = await api.patch(`/admin/users/${user.id}`, payload);
        toast.success("User updated");
        onSaved && onSaved({ updated: data });
      } else {
        const { data } = await api.post("/admin/users", payload);
        toast.success("User created");
        onSaved && onSaved({ created: data });
      }
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  return (
    <ModalShell open={open} onClose={onClose} maxWidth="max-w-xl" testId="user-modal">
      <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm" data-testid="user-close" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">{isEdit ? "Edit User" : "New User"}</div>
      <h2 className="font-display text-3xl uppercase font-bold tracking-tight">{isEdit ? user.name : "Create User"}</h2>

      <form onSubmit={submit} className="mt-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="user-name" />
          <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="user-email" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">Access</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" data-testid="user-role">
            <option value="admin">Admin — full access</option>
            <option value="partner">Partner — view / request / download</option>
            <option value="retail">Retail — view / request / download</option>
          </select>
        </div>
        <StateSelector value={form.states} onChange={(str) => setForm({ ...form, states: str })} testIdPrefix="user-state" />
        <button type="submit" disabled={busy} className="w-full bg-foreground text-background py-3 uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="user-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? <><Save className="h-3.5 w-3.5" /> Save Changes</> : "Create User"}
        </button>
      </form>
    </ModalShell>
  );
}

function Input({ label, value, onChange, className = "", testId, type = "text" }) {
  return (
    <div className={className}>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" data-testid={testId} />
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground capitalize" data-testid={testId}>
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
